import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Check, X } from "lucide-react";

const ValueProposition = () => {
  const { t } = useTranslation();
  const competitors = [
    { name: "WolfX", price: "89" },
    { name: "Cryptoninjas", price: "99" },
    { name: "Inner Circle", price: "70" },
    { name: "Predictum", price: "89" },
  ];

  const comparisonFeatures = [
    { feature: t('valueProposition.features.traders'), individual: "1", oraculus: "Multiple" },
    { feature: t('valueProposition.features.signals'), individual: "2-3", oraculus: "15+" },
    { feature: t('valueProposition.features.variety'), individual: t('valueProposition.values.limited'), oraculus: t('valueProposition.values.cryptos') },
    { feature: t('valueProposition.features.community'), individual: t('valueProposition.values.basic'), oraculus: t('valueProposition.values.activeComm') },
    { feature: t('valueProposition.features.tracking'), individual: false, oraculus: true },
    { feature: t('valueProposition.features.education'), individual: false, oraculus: true },
    { feature: t('valueProposition.features.autoTrade'), individual: false, oraculus: true },
    { feature: t('valueProposition.features.cost'), individual: "$70-199", oraculus: "$99" },
  ];

  return (
    <section className="py-20 relative overflow-hidden">
      <div className="container px-4 relative z-10">
        <div className="text-center mb-16 space-y-4">
          <h2 className="text-4xl md:text-5xl font-bold">
            Why Traders Choose <span className="bg-gradient-primary bg-clip-text text-transparent">Oraculus:X</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Paying <span className="font-semibold text-foreground">$500-600/month</span> for separate signal services? There's a smarter way. Get access to elite traders for one simple price.
          </p>
          <p className="text-2xl font-bold text-primary">
            One Platform. Multiple Experts. Maximum Profit Potential.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {competitors.map((competitor) => (
            <Card key={competitor.name} className="p-6 bg-card/30 backdrop-blur-xl border-border hover:border-primary/50 transition-colors">
              <div className="text-center space-y-2">
                <h3 className="text-xl font-semibold">{competitor.name}</h3>
                <p className="text-3xl font-bold text-primary">${competitor.price}</p>
                <p className="text-sm text-muted-foreground">{t('valueProposition.perMonth')}</p>
              </div>
            </Card>
          ))}
        </div>

        {/* Comparison Table */}
        <div className="mb-16">
          <h3 className="text-3xl font-bold text-center mb-8">
            Individual Traders vs <span className="gradient-x-animated">Oraculus:X</span> — The Clear Winner
          </h3>
          <Card className="overflow-hidden bg-card/30 backdrop-blur-xl border-border">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left p-4 font-semibold">{t('valueProposition.feature')}</th>
                    <th className="text-center p-4 font-semibold">{t('valueProposition.individualTrader')}</th>
                    <th className="text-center p-4 font-semibold bg-primary/10">
                      <span className="text-primary">Oraculus:X</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonFeatures.map((item, index) => (
                    <tr key={index} className="border-b border-border/50 hover:bg-secondary/20 transition-colors">
                      <td className="p-4 font-medium">{item.feature}</td>
                      <td className="p-4 text-center text-muted-foreground">
                        {typeof item.individual === 'boolean' ? (
                          item.individual ? (
                            <Check className="w-5 h-5 text-green-500 mx-auto" />
                          ) : (
                            <X className="w-5 h-5 text-red-500 mx-auto" />
                          )
                        ) : (
                          item.individual
                        )}
                      </td>
                      <td className="p-4 text-center bg-primary/5">
                        {typeof item.oraculus === 'boolean' ? (
                          item.oraculus ? (
                            <Check className="w-5 h-5 text-primary mx-auto" />
                          ) : (
                            <X className="w-5 h-5 text-red-500 mx-auto" />
                          )
                        ) : (
                          <span className="font-semibold text-primary">{item.oraculus}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>

        <Card className="p-8 md:p-12 bg-gradient-primary relative overflow-hidden">
          <div className="absolute inset-0 bg-card/80 backdrop-blur-2xl" />
          <div className="relative z-10 text-center space-y-6">
            <h3 className="text-3xl md:text-4xl font-bold">
              Stop Paying More for <span className="text-primary">Less</span>
            </h3>
            <div className="flex items-center justify-center gap-4">
              <span className="text-2xl text-muted-foreground line-through">$500-600</span>
              <span className="text-2xl font-semibold">→</span>
              <div className="bg-primary text-primary-foreground rounded-full px-8 py-4">
                <span className="text-5xl font-bold">$99</span>
              </div>
              <span className="text-2xl text-muted-foreground">per month</span>
            </div>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Why juggle multiple subscriptions when you can have it all? <span className="gradient-x-animated font-semibold">Elite professional traders</span>, unlimited signals, complete market coverage — all in one seamless platform.
            </p>
            <p className="text-muted-foreground">
              <a href="/features" className="text-primary hover:underline font-semibold">Explore Features & Details →</a>
            </p>
            <div className="flex flex-wrap justify-center gap-4 pt-4">
              {[
                t('valueProposition.badges.multipleTraders'),
                t('valueProposition.badges.liveSignals'),
                t('valueProposition.badges.riskManagement'),
                t('valueProposition.badges.support')
              ].map((feature) => (
                <div key={feature} className="flex items-center gap-2 bg-secondary/50 px-4 py-2 rounded-full">
                  <Check className="w-4 h-4 text-primary" />
                  <span className="text-sm">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </Card>
      </div>
    </section>
  );
};

export default ValueProposition;